import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useInfiniteQuery,
  useQuery,
  infiniteQueryOptions,
  type InfiniteData,
  queryOptions,
} from '@tanstack/react-query'
import {
  BuyAgainCategoriesQuery,
  CouponsSearchQuery,
  CouponsSearchQueryLegacy,
  GlobalSearchQuery,
  ProductsSearchQuery,
  type PurchaseHistoryQuery,
} from '@/features/shop/services/Search/constants'
import {
  type SearchCoupons,
  type SearchCouponsLegacy,
  type SearchParams,
  type SearchResults,
  type Facet,
} from '@/features/shop/services/Search/types'
import {
  type StoreParamsAsKey,
  useStoreParams,
} from '@/features/shared/utils/dataFetching/storeParams'
import { type Product } from '@/features/shop/services/Product/types'
import { trackProductsSearched } from '@/features/shared/analytics/products'
import {
  fetchBuyAgainCategories,
  fetchGlobalSearch,
  fetchMoreLikeThisItemsSearch,
  fetchSearchCoupons,
  fetchSearchCouponsLegacy,
  fetchSearchProducts,
} from '@/features/shop/services/Search/fetchers'
import { infinitePurchaseHistoryQueryOptions } from '@/features/shop/services/Search/queryOptions'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { useMedalliaSearchParams } from '@/features/shop/services/Search/hooks'
import { getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { useUserId } from '@/features/account/services/User/hooks'
import { useFeatureFlags } from '@/features/shared/services/FeatureFlags/useFeatureFlags'
import { useSearchQueryParams, useValidSearchParams } from '@/features/shared/hooks/search'
import { type Shelf } from '@/features/shop/services/Shelves/types'
import { type Coupon } from '@/features/shop/services/Coupons/types'
import { findCurrentCategoryByParams } from '@/features/shop/services/Search/utils'
import { useParams } from 'next/navigation'
import { trackSponsoredContentLoaded } from '@/features/shared/analytics/promotions'

/**
 * useInfiniteQueryCouponsSearch
 */
export type CouponsSearchQueryKey = readonly [
  typeof CouponsSearchQuery,
  StoreParamsAsKey['storeParams'] & { categoryId: string | undefined },
]

export type CouponsSearchQueryKeyLegacy = readonly [
  typeof CouponsSearchQueryLegacy,
  StoreParamsAsKey['storeParams'] & { categoryId: string | undefined },
]

export type NormalizedCouponsSearchPage = {
  totalCount: number
  facets: Facet[]
  coupons: Coupon[]
}

const isLegacyCouponsSearchPage = (
  page: SearchCoupons | SearchCouponsLegacy,
): page is SearchCouponsLegacy => 'total_coupons' in page

const normalizeCouponsSearchPage = (
  page: SearchCoupons | SearchCouponsLegacy,
): NormalizedCouponsSearchPage => {
  if (isLegacyCouponsSearchPage(page)) {
    return {
      totalCount: page.total_coupons,
      facets: page.facets,
      coupons: page.coupons,
    }
  }

  return {
    totalCount: page.pagination.total_items,
    facets: page.facets,
    coupons: page.result,
  }
}

const normalizeCouponsSearchData = (data: InfiniteData<SearchCoupons | SearchCouponsLegacy>) => ({
  ...data,
  pages: data.pages.map((page) => normalizeCouponsSearchPage(page)),
})

const couponsSearchQueryOptions = ({
  storeParams,
  categoryId,
}: StoreParamsAsKey & { categoryId: string | undefined }) =>
  infiniteQueryOptions({
    queryKey: [CouponsSearchQuery, { ...storeParams, categoryId }] as const,
    initialPageParam: '',
    queryFn: fetchSearchCoupons,
    select: (data) => normalizeCouponsSearchData(data),
    getNextPageParam: (lastPage) => lastPage.pagination.after_cursor || undefined,
    placeholderData: (previousData) => previousData,
  })

const couponsSearchQueryOptionsLegacy = ({
  storeParams,
  categoryId,
}: StoreParamsAsKey & { categoryId: string | undefined }) =>
  infiniteQueryOptions({
    queryKey: [CouponsSearchQueryLegacy, { ...storeParams, categoryId }] as const,
    initialPageParam: { page: 0, coupon_after_key: '' },
    queryFn: fetchSearchCouponsLegacy,
    select: (data) => normalizeCouponsSearchData(data),
    getNextPageParam: (lastPage) => {
      const { total_coupons, page, coupons_per_page, coupon_after_key } = lastPage
      if (total_coupons - page * coupons_per_page <= 0) return undefined
      return { coupon_after_key, page: page + 1 }
    },
    placeholderData: (previousData) => previousData,
  })

export const useInfiniteQueryCouponsSearch = () => {
  const storeParams = useStoreParams()
  const searchParams = useSearchQueryParams()
  const { flags, status } = useFeatureFlags()
  const { store_location_id } = storeParams
  const categoryId = searchParams.category_id ? String(searchParams.category_id) : undefined

  const legacyQuery = useInfiniteQuery({
    ...couponsSearchQueryOptionsLegacy({
      storeParams,
      categoryId: categoryId ?? '',
    }),
    enabled: Boolean(store_location_id) && status !== 'pending' && !flags.coupons_search_migration,
  })

  const modernQuery = useInfiniteQuery({
    ...couponsSearchQueryOptions({
      storeParams,
      categoryId,
    }),
    enabled: Boolean(store_location_id) && status !== 'pending' && flags.coupons_search_migration,
  })

  return flags.coupons_search_migration ? modernQuery : legacyQuery
}

const MoreLikeThisSearchQuery = 'More like this search'
type MoreLikeThisSearchQueryParams = { product_id?: number; size?: number }

export type MoreLikeThisSearchQueryKey = readonly [
  typeof MoreLikeThisSearchQuery,
  Parameters<typeof moreLikeThisItemsSearchQueryOptions>[0],
]
const moreLikeThisItemsSearchQueryOptions = (
  params: StoreParamsAsKey & MoreLikeThisSearchQueryParams,
) =>
  queryOptions({
    queryKey: [MoreLikeThisSearchQuery, params] as const,
    queryFn: fetchMoreLikeThisItemsSearch,
  })

export const useQueryMoreLikeThisItemsSearch = ({
  product_id,
  size,
}: MoreLikeThisSearchQueryParams) => {
  const storeParams = useStoreParams()
  return useQuery({
    ...moreLikeThisItemsSearchQueryOptions({
      size,
      product_id,
      storeParams,
    }),
    enabled: Boolean(product_id),
  })
}

const useInfiniteQuerySearchResults = (data?: InfiniteData<SearchResults>) => {
  return useMemo<SearchResults>(() => {
    const result = data?.pages.at(-1)
    const products = data?.pages.reduce<Product[]>((acc, page) => {
      return [...acc, ...(page.products || [])]
    }, [])
    const shelves = data?.pages.reduce<Shelf[]>((acc, page) => {
      return [...acc, ...page.shelves]
    }, [])
    const coupons = data?.pages.reduce<Coupon[]>((acc, page) => {
      return [...acc, ...page.coupons]
    }, [])

    result?.product_beacons?.onLoadBeacons?.forEach((beaconUrl) => {
      trackSponsoredContentLoaded({
        beaconUrl,
        featuredTransactionID: result.beacons?.featuredTransactionID ?? '',
      })
    })

    return {
      ...result,
      products: products || [],
      facets: result?.facets || [],
      coupons: coupons || [],
      shelves: shelves || [],
      beacons: result?.beacons || {},
      endcapsBySlotPos: result?.endcapsBySlotPos || {},
      page: result?.page || 0,
      total_hits: result?.total_hits || 0,
      hits_per_page: result?.hits_per_page || 0,
    }
  }, [data?.pages])
}

/**
 * useInfiniteQuerySearch
 */
const getSearchQueryKey = ({
  params,
  storeParams,
  pageSize,
  backupProductId,
}: StoreParamsAsKey & {
  params: SearchParams
  pageSize: number
  backupProductId?: number
}) => {
  return [
    ProductsSearchQuery,
    {
      storeParams,
      pageSize,
      ...(backupProductId ? { backupProductId, params: { query: params.query } } : { params }),
    },
  ] as const
}

export type SearchQueryKey = ReturnType<typeof getSearchQueryKey>

export const infiniteSearchQueryOptions = (params: Parameters<typeof getSearchQueryKey>[0]) =>
  infiniteQueryOptions({
    queryKey: getSearchQueryKey(params),
    queryFn: fetchSearchProducts,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { total_hits, page, hits_per_page } = lastPage
      if (total_hits - page * hits_per_page <= 0) return undefined
      return page + 1
    },
    placeholderData: (previousData) => previousData,
    retry: (errorCount, error) =>
      // Retry only for network errors of 500 status codes until BE completes investigation
      // https://shipt.slack.com/archives/CF6Q7N2CU/p1732127258775699
      getErrorStatus(error) === 500 && errorCount < 2,
  })
type StoreSlugParams = {
  store_slug: string
}
export const useInfiniteQuerySearch = ({
  query,
  searchType,
  enabled = true,
  /**
   * Only pass shouldTrack: true if you want the hook to track the search event.
   * We only want to track the search event once per API call per page, but it is
   * possible that this hook gets mounted more than one time on the same page.
   */
  shouldTrack,
  pageSize = 48,
  backupProductId,
}: {
  query?: string
  searchType?: string
  enabled?: boolean
  shouldTrack?: boolean
  pageSize?: number
  backupProductId?: number
} = {}) => {
  const storeParams = useStoreParams()
  const { store_location_id } = storeParams
  const searchParams = useValidSearchParams()
  const { store_slug } = useParams<StoreSlugParams>() ?? {}
  const {
    error,
    data,
    isSuccess,
    isFetched,
    isFetching,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...infiniteSearchQueryOptions({
      params: { ...searchParams, ...(query && { query }) },
      storeParams,
      pageSize,
      backupProductId,
    }),
    enabled: Boolean(store_location_id && enabled),
  })

  const searchResults = useInfiniteQuerySearchResults(data)

  useEffect(() => {
    if (isFetched && shouldTrack) {
      trackProductsSearched(
        /* This analytic event will fire for each page of the products search query.
        Each call should only include the products for the respective page number of results
        instead of current page + all products from previous pages. */
        { ...searchResults, products: data?.pages.at(-1)?.products },
        { ...searchParams, ...(query && { query }), searchType },
      )
    }
  }, [isFetched, shouldTrack, searchResults, query, searchParams, searchType, data?.pages])
  useMedalliaSearchParams({
    ...storeParams,
    ...searchParams,
    searchType,
    product_category:
      findCurrentCategoryByParams(searchResults.facets, searchParams)?.name ??
      searchParams.category_name,
    retailer: store_slug,
  })

  return {
    data,
    error,
    isSuccess,
    isFetched,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    searchResults,
  }
}

/**
 * useQueryBuyAgainCategories
 */
export const buyAgainCategoriesQueryOptions = (params: StoreParamsAsKey) =>
  queryOptions({
    queryKey: [BuyAgainCategoriesQuery, params.storeParams] as const,
    queryFn: fetchBuyAgainCategories,
  })

export const useQueryBuyAgainCategories = () => {
  const storeParams = useStoreParams()
  const { store_location_id } = storeParams
  const user_id = useUserId()
  return useQuery({
    ...buyAgainCategoriesQueryOptions({ storeParams }),
    enabled: Boolean(user_id && store_location_id),
  })
}

export type BuyAgainCategoriesQueryKey = readonly [
  typeof BuyAgainCategoriesQuery,
  StoreParamsAsKey['storeParams'],
]

export const useInfiniteQueryPurchaseHistory = ({
  shouldTrack,
}: {
  shouldTrack?: boolean
} = {}) => {
  const storeParams = useStoreParams()
  const { store_location_id } = storeParams
  const user_id = useUserId()
  const params = useValidSearchParams()

  const {
    error,
    data,
    isSuccess,
    isFetched,
    isFetching,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...infinitePurchaseHistoryQueryOptions({ params, storeParams }),
    enabled: Boolean(user_id && store_location_id),
  })
  const searchResults = useInfiniteQuerySearchResults(data)

  useEffect(() => {
    if (isFetched && shouldTrack) {
      trackProductsSearched(searchResults, { ...params, searchType: 'typed' })
    }
  }, [isFetched, shouldTrack, searchResults, params])

  return {
    data,
    error,
    isSuccess,
    isFetched,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    searchResults,
  }
}

export type PurchaseHistoryQueryKey = readonly [
  typeof PurchaseHistoryQuery,
  Parameters<typeof infinitePurchaseHistoryQueryOptions>[0],
]

/**
 * useQueryGlobalSearch
 */
export type GlobalSearchQueryKey = readonly [
  typeof GlobalSearchQuery,
  Parameters<typeof globalSearchQueryOptions>[0],
]
const globalSearchQueryOptions = (params: {
  userId: number
  query: string | undefined
  contexts: string[]
  category_ids: string[] | undefined
}) =>
  queryOptions({
    queryKey: [GlobalSearchQuery, params] as const,
    queryFn: fetchGlobalSearch,
  })

export const useQueryGlobalSearch = ({
  searchType,
}: {
  searchType?: string
} = {}) => {
  const userId = useUserId() || 0
  const {
    query,
    contexts: selectedContexts = [],
    category_ids,
    category_name,
  } = useValidSearchParams()
  const { data: stores } = useQueryShoppingStores()
  const userContexts =
    stores?.delivery_stores.map((sc) => `l${sc.store_location_id}_m${sc.metro_id}_s${sc.id}`) ?? []
  const contexts = selectedContexts.length ? selectedContexts : userContexts
  useMedalliaSearchParams({
    query,
    product_category: category_name,
    searchType,
  })
  return useQuery({
    ...globalSearchQueryOptions({
      userId,
      query,
      contexts,
      category_ids,
    }),
    enabled: contexts.length > 0 && Boolean(query || category_ids),
  })
}

export const useProductAutocompleteSearch = (backupProductId?: number) => {
  const [selectedQuery, setSelectedQuery] = useState({
    query: '',
    searchType: '',
  })
  const { searchResults, isFetching } = useInfiniteQuerySearch({
    shouldTrack: true,
    query: selectedQuery.query,
    searchType: selectedQuery.searchType,
    enabled: Boolean(selectedQuery.query),
    backupProductId,
  })

  const searchProducts = searchResults.products ?? []

  const handleSearchSubmit = useCallback(
    (query: string, { suggestion = false }) => {
      if (!isFetching) {
        setSelectedQuery({
          query,
          searchType: suggestion ? 'autocomplete' : 'typed',
        })
      }
    },
    [isFetching],
  )

  return {
    isFetching,
    searchProducts,
    handleSearchSubmit,
    selectedQuery,
  }
}
